import { useWorkspaceStore } from '@/stores/workspaceStore';
import { workspaceApi, Workspace } from '@/lib/workspaceApi';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Label } from '@fyos/components';
import { Plus, X, Trash2 } from 'lucide-react';
import AppNav from '@/components/AppNav';
import WorkspaceSkeleton from '@/components/WorkspaceSkeleton';
import RichTextEditor from '@/components/RichTextEditor';
import MarkdownContent from '@/components/MarkdownContent';
import ConfirmDialog from '@/components/ConfirmDialog';

export default function DashboardPage() {
  const { workspaces, setWorkspaces, setCurrentWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Workspace | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const data = await workspaceApi.getAll();
      setWorkspaces(data);
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) {
      setError('Workspace name is required');
      return;
    }

    try {
      setCreating(true);
      setError('');
      const workspace = await workspaceApi.create({
        name: newWorkspaceName,
        description: newWorkspaceDescription || undefined,
      });
      setWorkspaces([workspace, ...workspaces]);
      setShowCreateModal(false);
      setNewWorkspaceName('');
      setNewWorkspaceDescription('');
      setCurrentWorkspace(workspace);
      navigate(`/roadmap/${workspace.id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const handleWorkspaceClick = (workspace: Workspace) => {
    setCurrentWorkspace(workspace);
    navigate(`/roadmap/${workspace.id}`);
  };

  const handleDeleteWorkspace = (e: React.MouseEvent, workspace: Workspace) => {
    e.stopPropagation();
    setDeleteTarget(workspace);
  };

  const confirmDeleteWorkspace = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await workspaceApi.delete(deleteTarget.id);
      setWorkspaces(workspaces.filter(w => w.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      console.error('Failed to delete workspace:', err);
      console.error(err.response?.data?.message || 'Failed to delete workspace');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setNewWorkspaceName('');
    setNewWorkspaceDescription('');
    setError('');
  };

  const filteredWorkspaces = workspaces.filter(w => !searchQuery.trim() || w.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AppNav
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search workspaces..."
      />
      <main className="max-w-8xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Your Workspaces</h2>
            <Button size="sm" onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4" />
              Create Workspace
            </Button>
          </div>

          {loading ? (
            <WorkspaceSkeleton />
          ) : workspaces.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-md shadow dark:bg-gray-800">
              <p className="text-gray-600 dark:text-gray-400 mb-4">No workspaces yet</p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                Create Your First Workspace
              </Button>
            </div>
          ) : filteredWorkspaces.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-md shadow dark:bg-gray-800">
              <p className="text-gray-500 dark:text-gray-400 mb-1">No workspaces match your search</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Try a different search term or{' '}
                <button className="text-brand-500 hover:underline" onClick={() => setSearchQuery('')}>clear the search</button>
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredWorkspaces.map((workspace) => (
                <div
                  key={workspace.id}
                  onClick={() => handleWorkspaceClick(workspace)}
                  className="bg-white p-6 rounded-lg shadow hover:shadow-lg cursor-pointer transition-shadow dark:bg-gray-800 dark:shadow-gray-900"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{workspace.name}</h3>
                    <div className="flex items-center gap-2">
                      {workspace.is_owner && (
                        <span className="text-xs bg-brand-100 text-brand-800 px-2 py-1 rounded dark:bg-brand-900/30 dark:text-brand-300">Owner</span>
                      )}
                      {workspace.is_owner && (
                        <button
                          onClick={(e) => handleDeleteWorkspace(e, workspace)}
                          className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                          title="Delete workspace"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {workspace.description && (
                    <MarkdownContent
                      content={workspace.description}
                      className="text-sm text-gray-600 dark:text-gray-400 mb-3 prose prose-sm dark:prose-invert max-w-none line-clamp-3"
                    />
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{workspace.role}</span>
                    <span>{workspace.is_public ? 'Public' : 'Private'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div
            className="fixed inset-0 bg-gray-400/50 dark:bg-black/60 backdrop-blur-sm"
            onClick={handleCloseModal}
            aria-hidden="true"
          />
          <div className="relative z-50 w-full max-w-[768px]" role="dialog" aria-modal="true">
            <div className="relative rounded-2xl bg-white p-6 shadow-xl sm:p-8 dark:bg-gray-800">
              <button
                type="button"
                onClick={handleCloseModal}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create New Workspace</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Set up a new workspace for your team.</p>
              </div>

              <form onSubmit={handleCreateWorkspace}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="workspace-name" required>
                      Workspace Name
                    </Label>
                    <Input
                      id="workspace-name"
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      placeholder="My Awesome Project"
                      disabled={creating}
                      autoFocus
                    />
                  </div>

                  <div>
                    <Label htmlFor="workspace-description">Description</Label>
                    <RichTextEditor
                      content={newWorkspaceDescription}
                      onChange={setNewWorkspaceDescription}
                      placeholder="What's this workspace about?"
                      disabled={creating}
                      minHeight="350px"
                    />
                  </div>

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseModal}
                    disabled={creating}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? 'Creating...' : 'Create Workspace'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}
        title="Delete Workspace"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will permanently remove all items, comments, and data. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDeleteWorkspace}
        loading={deleting}
      />
    </div>
  );
}
